import { country as countryList } from "../constants/country.js";
import { getCountryCode } from "./country.js";

/**
 * Standard country-specific address formatting and validation configuration
 */
const COUNTRY_ADDRESS_CONFIGS = {
  // United Kingdom
  GB: {
    postalLabel: "Postcode",
    postalPlaceholder: "e.g. RG24 9TJ",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: false,
    sanitizePostal: (val) => {
      // Allow letters, digits, and space, max 8 chars
      return val.replace(/[^a-zA-Z0-9 ]/g, "").toUpperCase().slice(0, 8);
    },
    formatPostal: (val) => {
      if (!val) return "";
      const trimmed = val.trim().toUpperCase();
      const clean = trimmed.replace(/\s+/g, "");
      // UK inward code is always the last 3 characters (1 digit, 2 letters)
      if (clean.length >= 5 && clean.length <= 7) {
        return `${clean.slice(0, -3)} ${clean.slice(-3)}`;
      }
      return trimmed;
    },
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postcode field";
      const trimmed = val.trim().toUpperCase();
      // Royal Mail UK postcode regex (case-insensitive, allows space or formatted)
      const ukRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
      if (!ukRegex.test(trimmed)) {
        return "Please enter a valid UK postcode (e.g. RG24 9TJ or AL5 4UT)";
      }
      return "";
    },
  },

  // United States
  US: {
    postalLabel: "Zip Code",
    postalPlaceholder: "e.g. 90210",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => {
      // Allow digits and optional hyphen, max 10 chars (e.g. 90210-1234)
      return val.replace(/[^0-9-]/g, "").slice(0, 10);
    },
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the zip code field";
      const trimmed = val.trim();
      const usRegex = /^\d{5}(-\d{4})?$/;
      if (!usRegex.test(trimmed)) {
        return "Zip Code must be 5 digits (e.g. 90210)";
      }
      return "";
    },
  },

  // South Africa
  ZA: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 8001",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => {
      // 4 digits numeric
      return val.replace(/\D/g, "").slice(0, 4);
    },
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      const trimmed = val.trim();
      if (!/^\d{4}$/.test(trimmed)) {
        return "Postal Code must be a 4-digit number (e.g. 8001)";
      }
      return "";
    },
  },

  // Canada
  CA: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. K1A 0B1",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => {
      return val.replace(/[^a-zA-Z0-9 ]/g, "").toUpperCase().slice(0, 7);
    },
    formatPostal: (val) => {
      if (!val) return "";
      const clean = val.replace(/\s+/g, "").toUpperCase();
      if (clean.length === 6) {
        return `${clean.slice(0, 3)} ${clean.slice(3)}`;
      }
      return val.trim().toUpperCase();
    },
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      const trimmed = val.trim().toUpperCase();
      const caRegex = /^[A-CEGHJ-NPR-TV-Z][0-9][A-CEGHJ-NPR-TV-Z]\s?[0-9][A-CEGHJ-NPR-TV-Z][0-9]$/i;
      if (!caRegex.test(trimmed)) {
        return "Please enter a valid postal code (e.g. K1A 0B1)";
      }
      return "";
    },
  },

  // Australia
  AU: {
    postalLabel: "Postcode",
    postalPlaceholder: "e.g. 2000",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => val.replace(/\D/g, "").slice(0, 4),
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postcode field";
      if (!/^\d{4}$/.test(val.trim())) {
        return "Postcode must be a 4-digit number (e.g. 2000)";
      }
      return "";
    },
  },

  // Ireland
  IE: {
    postalLabel: "Eircode / Postcode",
    postalPlaceholder: "e.g. D02 X285",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: false,
    sanitizePostal: (val) => {
      return val.replace(/[^a-zA-Z0-9 ]/g, "").toUpperCase().slice(0, 8);
    },
    formatPostal: (val) => {
      if (!val) return "";
      const clean = val.replace(/\s+/g, "").toUpperCase();
      if (clean.length === 7) {
        return `${clean.slice(0, 3)} ${clean.slice(3)}`;
      }
      return val.trim().toUpperCase();
    },
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      const clean = val.replace(/\s+/g, "").toUpperCase();
      if (!/^[A-Za-z0-9]{7}$/.test(clean)) {
        return "Please enter a valid Eircode (e.g. D02 X285)";
      }
      return "";
    },
  },

  // Netherlands
  NL: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 1012 AB",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: false,
    sanitizePostal: (val) => {
      return val.replace(/[^a-zA-Z0-9 ]/g, "").toUpperCase().slice(0, 7);
    },
    formatPostal: (val) => {
      if (!val) return "";
      const clean = val.replace(/\s+/g, "").toUpperCase();
      if (clean.length === 6 && /^\d{4}[A-Z]{2}$/.test(clean)) {
        return `${clean.slice(0, 4)} ${clean.slice(4)}`;
      }
      return val.trim().toUpperCase();
    },
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      const clean = val.replace(/\s+/g, "").toUpperCase();
      if (!/^[1-9][0-9]{3}[A-Z]{2}$/.test(clean)) {
        return "Please enter a valid postal code (e.g. 1012 AB)";
      }
      return "";
    },
  },

  // Germany
  DE: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 10115",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: false,
    sanitizePostal: (val) => val.replace(/\D/g, "").slice(0, 5),
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      if (!/^\d{5}$/.test(val.trim())) return "Postal Code must be 5 digits (e.g. 10115)";
      return "";
    },
  },

  // France
  FR: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 75001",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: false,
    sanitizePostal: (val) => val.replace(/\D/g, "").slice(0, 5),
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      if (!/^\d{5}$/.test(val.trim())) return "Postal Code must be 5 digits (e.g. 75001)";
      return "";
    },
  },

  // Spain
  ES: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 28001",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => val.replace(/\D/g, "").slice(0, 5),
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      if (!/^\d{5}$/.test(val.trim())) return "Postal Code must be 5 digits (e.g. 28001)";
      return "";
    },
  },

  // Italy
  IT: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 00118",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => val.replace(/\D/g, "").slice(0, 5),
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      if (!/^\d{5}$/.test(val.trim())) return "Postal Code must be 5 digits (e.g. 00118)";
      return "";
    },
  },

  // India
  IN: {
    postalLabel: "PIN Code",
    postalPlaceholder: "e.g. 110001",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => val.replace(/\D/g, "").slice(0, 6),
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the PIN code field";
      if (!/^\d{6}$/.test(val.trim())) return "PIN Code must be 6 digits (e.g. 110001)";
      return "";
    },
  },

  // Japan
  JP: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 100-0001",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => val.replace(/[^0-9-]/g, "").slice(0, 8),
    formatPostal: (val) => {
      const clean = val.replace(/\D/g, "");
      if (clean.length === 7) return `${clean.slice(0, 3)}-${clean.slice(3)}`;
      return val.trim();
    },
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      const clean = val.replace(/\D/g, "");
      if (clean.length !== 7) return "Postal Code must be 7 digits (e.g. 100-0001)";
      return "";
    },
  },

  // Brazil
  BR: {
    postalLabel: "Postal Code (CEP)",
    postalPlaceholder: "e.g. 01001-000",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => val.replace(/[^0-9-]/g, "").slice(0, 9),
    formatPostal: (val) => {
      const clean = val.replace(/\D/g, "");
      if (clean.length === 8) return `${clean.slice(0, 5)}-${clean.slice(5)}`;
      return val.trim();
    },
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      const clean = val.replace(/\D/g, "");
      if (clean.length !== 8) return "CEP must be 8 digits (e.g. 01001-000)";
      return "";
    },
  },

  // New Zealand
  NZ: {
    postalLabel: "Postcode",
    postalPlaceholder: "e.g. 1010",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: true,
    sanitizePostal: (val) => val.replace(/\D/g, "").slice(0, 4),
    formatPostal: (val) => val.trim(),
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postcode field";
      if (!/^\d{4}$/.test(val.trim())) return "Postcode must be 4 digits (e.0. 1010)";
      return "";
    },
  },

  // Poland
  PL: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 00-001",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: false,
    sanitizePostal: (val) => val.replace(/[^0-9-]/g, "").slice(0, 6),
    formatPostal: (val) => {
      const clean = val.replace(/\D/g, "");
      if (clean.length === 5) return `${clean.slice(0, 2)}-${clean.slice(2)}`;
      return val.trim();
    },
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      if (!/^\d{2}-?\d{3}$/.test(val.trim())) return "Postal Code must be formatted as 00-000";
      return "";
    },
  },

  // Portugal
  PT: {
    postalLabel: "Postal Code",
    postalPlaceholder: "e.g. 1000-001",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: false,
    sanitizePostal: (val) => val.replace(/[^0-9-]/g, "").slice(0, 8),
    formatPostal: (val) => {
      const clean = val.replace(/\D/g, "");
      if (clean.length === 7) return `${clean.slice(0, 4)}-${clean.slice(4)}`;
      return val.trim();
    },
    validatePostal: (val) => {
      if (!val || !val.trim()) return "Please fill in the postal code field";
      if (!/^\d{4}-?\d{3}$/.test(val.trim())) return "Postal Code must be formatted as 0000-000";
      return "";
    },
  },

  // United Arab Emirates (no postal code system)
  AE: {
    postalLabel: "Postal Code (Optional)",
    postalPlaceholder: "Optional",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: false,
    isStateRequired: false,
    sanitizePostal: (val) => val.replace(/[^a-zA-Z0-9 -]/g, "").slice(0, 10),
    formatPostal: (val) => val.trim(),
    validatePostal: () => "",
  },

  // Qatar (no postal code system)
  QA: {
    postalLabel: "Postal Code (Optional)",
    postalPlaceholder: "Optional",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: false,
    isStateRequired: false,
    sanitizePostal: (val) => val.replace(/[^a-zA-Z0-9 -]/g, "").slice(0, 10),
    formatPostal: (val) => val.trim(),
    validatePostal: () => "",
  },

  // Hong Kong (no postal code system)
  HK: {
    postalLabel: "Postal Code (Optional)",
    postalPlaceholder: "Optional",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: false,
    isStateRequired: false,
    sanitizePostal: (val) => val.replace(/[^a-zA-Z0-9 -]/g, "").slice(0, 10),
    formatPostal: (val) => val.trim(),
    validatePostal: () => "",
  },
};

/**
 * Resolve ISO Alpha-2 code for a country
 */
export const resolveCountryIso = (countryInput) => {
  if (!countryInput) return null;
  const inputStr = countryInput.toString().trim();
  if (!inputStr) return null;

  // Direct 2-letter uppercase match
  if (inputStr.length === 2 && /^[A-Za-z]{2}$/.test(inputStr)) {
    return inputStr.toUpperCase();
  }

  // Check country list in constants
  const matchedInList = countryList.find(
    (c) => c.country.toLowerCase() === inputStr.toLowerCase() || c.iso.toLowerCase() === inputStr.toLowerCase()
  );
  if (matchedInList && matchedInList.iso) {
    return matchedInList.iso.toUpperCase();
  }

  // Fallback to helper
  const helperCode = getCountryCode(inputStr);
  if (helperCode) return helperCode.toUpperCase();

  return null;
};

/**
 * Get country address format configuration
 */
export const getAddressConfig = (countryInput) => {
  const iso = resolveCountryIso(countryInput);
  const matchedConfig = iso ? COUNTRY_ADDRESS_CONFIGS[iso] : null;

  if (matchedConfig) {
    return {
      countryIso: iso,
      ...matchedConfig,
      stateLabel: "State",
      statePlaceholder: "State",
      validateState: (stateVal) => {
        if (matchedConfig.isStateRequired && (!stateVal || !stateVal.toString().trim())) {
          return "Please fill in the state field";
        }
        return "";
      },
    };
  }

  // Universal Fallback Configuration for remaining countries
  return {
    countryIso: iso || "",
    postalLabel: "Postal Code",
    postalPlaceholder: "Postal Code",
    stateLabel: "State",
    statePlaceholder: "State",
    isPostalRequired: true,
    isStateRequired: false,
    sanitizePostal: (val) => {
      // Allow alphanumeric, space, and hyphen, max 12 chars
      return val.replace(/[^a-zA-Z0-9 -]/g, "").toUpperCase().slice(0, 12);
    },
    formatPostal: (val) => val.trim().toUpperCase(),
    validatePostal: (val) => {
      if (!val || !val.toString().trim()) {
        return "Please fill in the postal code field";
      }
      const trimmed = val.toString().trim();
      if (trimmed.length < 2 || trimmed.length > 12) {
        return "Please enter a valid postal code";
      }
      return "";
    },
    validateState: (stateVal) => {
      // Optional for universal fallback to not block users in countries without subdivisions
      return "";
    },
  };
};
